// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    address = "address",
    tel1 = "tel1",
    tel2 = "tel2",
    fax = "fax",
    email = "email",
}

interface OwnProps {
    fields: {
        [fields.address]: string;
        [fields.tel1]: string;
        [fields.tel2]: string;
        [fields.fax]: string;
        [fields.email]: string;
    };
}

type Props = OwnProps;

export interface GetInTouchStyles {
    icon?: InjectableCss;
    text?: InjectableCss;
    contactLink?: InjectableCss;
    infoWrapper?: InjectableCss;
}

export const getInTouchStyles: GetInTouchStyles = {
    contactLink: {
        css: css`
            margin-bottom: ${fontConfig.spacing.heading};
            span {
                font-weight: ${fontConfig.weight.semiBold};
            }
        `,
    },
    infoWrapper: {
        css: css`
            margin-bottom: ${fontConfig.spacing.heading};
            display: flex;
            flex-direction: row;
        `,
    },
};

const styles = getInTouchStyles;

const GetInTouch = ({ fields }: Props) => {
    const { email, tel1, tel2, fax, address } = fields;
    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-direction: column;
                margin-top: 15px;
                margin-bottom: 15px;
                span {
                    margin-left: 12px;
                }
                svg {
                    color: ${getColor("tertiary")};
                    fill: ${getColor("tertiary")};
                    margin-top: 3px;
                }
            `}
        >
            <Typography variant="h6">{translate("Let’s Get in Touch")}</Typography>
            {address && (
                <StyledWrapper {...styles.infoWrapper}>
                    <Icon src="Location" size={20} />
                    <Typography>{parse(address)}</Typography>
                </StyledWrapper>
            )}

            {tel1 && (
                <StyledWrapper {...styles.infoWrapper}>
                    <Icon src="Call" size={20} />
                    <StyledWrapper
                        css={css`
                            display: flex;
                            flex-direction: column;
                            margin-left: 15px;
                        `}
                    >
                        <a href={`tel:${tel1}`}>{tel1}</a>
                        {tel2 && <a href={`tel:${tel2}`}>{tel2}</a>}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {fax && (
                <StyledWrapper {...styles.infoWrapper}>
                    <Icon src="Print" size={20} />
                    <Typography>{fax}</Typography>
                </StyledWrapper>
            )}
            {email && (
                <StyledWrapper {...styles.infoWrapper}>
                    <Icon src="Mail" size={20} />

                    <Link
                        href={`mailto: ${email}`}
                        typographyProps={{
                            color: "common.ui1",
                        }}
                        css={css`
                            span {
                                color: ${getColor("common.ui1")} !important;
                            }
                        `}
                    >
                        {email}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GetInTouch,
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.address,
                displayName: "address",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.tel1,
                displayName: "Phone 1",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.tel2,
                displayName: "Phone 2",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.fax,
                displayName: "Fax",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
